import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Shield, Users, Activity, TrendingUp, BarChart3, Clock, 
  Zap, Brain, Stethoscope, Download, RefreshCw, Calendar,
  AlertTriangle, CheckCircle, ArrowUp, ArrowDown,
  ShieldPlus, ShieldMinus, Trash2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { CotationAdminPanel } from '../components/CotationAdminPanel';
import { BdpmAdminPanel } from '../components/BdpmAdminPanel';
import { ContributionsAdminPanel } from '../components/ContributionsAdminPanel';
import { BlogAdminPanel } from '../components/BlogAdminPanel';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Color palette
const COLORS = {
  primary: '#3b82f6',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#06b6d4',
  chart: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4']
};

const URGENCY_COLORS = {
  '1': '#10b981', // Green - Low
  '2': '#3b82f6', // Blue - Normal
  '3': '#f59e0b', // Orange - Moderate
  '4': '#ef4444', // Red - High
  '5': '#991b1b', // Dark Red - Critical
};

export const AdminPage = () => {
  const { user: currentUser } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState(30);
  const [busyUserId, setBusyUserId] = useState(null);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, userAnalyticsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/analytics?days=${period}`),
        axios.get(`${API}/admin/analytics/users?days=${period}`),
        axios.get(`${API}/admin/users`)
      ]);
      setAnalytics(analyticsRes.data);
      setUserAnalytics(userAnalyticsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès réservé aux administrateurs');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const refreshData = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Données actualisées');
  };

  const updateRole = async (user, newRole) => {
    const label = newRole === 'admin' ? 'promouvoir en administrateur' : 'rétrograder en médecin';
    if (!window.confirm(`Voulez-vous ${label} "${user.full_name}" ?`)) return;
    setBusyUserId(user.id);
    try {
      await axios.patch(`${API}/admin/users/${user.id}/role`, { role: newRole });
      toast.success(`"${user.full_name}" — rôle mis à jour`);
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification du rôle');
    } finally {
      setBusyUserId(null);
    }
  };

  const removeUser = async (user) => {
    if (!window.confirm(`Supprimer définitivement le compte de "${user.full_name}" (${user.email}) ?`)) return;
    setBusyUserId(user.id);
    try {
      await axios.delete(`${API}/admin/users/${user.id}`);
      toast.success(`Compte de "${user.full_name}" supprimé`);
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setBusyUserId(null);
    }
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/export?days=${period}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hippocrate_analytics_${period}days.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const hourlyData = analytics?.hourly_distribution 
    ? Object.entries(analytics.hourly_distribution)
        .map(([hour, count]) => ({ hour: `${hour}h`, count }))
        .sort((a, b) => parseInt(a.hour) - parseInt(b.hour))
    : [];

  const featureData = analytics?.feature_usage
    ? Object.entries(analytics.feature_usage)
        .map(([name, count]) => ({ 
          name: name === 'login' ? 'Connexions' : 
                name === 'diagnosis' ? 'Diagnostics' :
                name === 'dermato' ? 'Dermato' :
                name === 'search' ? 'Recherches' : name,
          count 
        }))
        .slice(0, 6)
    : [];

  const urgencyData = analytics?.urgency_distribution
    ? Object.entries(analytics.urgency_distribution).map(([level, count]) => ({
        name: `Niveau ${level}`,
        value: count,
        level
      }))
    : [];

  const dailyData = userAnalytics?.daily_activity || [];

  // Calculate trends
  const diagnosisTrend = analytics?.diagnoses_today > 0 ? 
    ((analytics.diagnoses_today / (analytics.diagnoses_week / 7)) - 1) * 100 : 0;

  return (
    <div data-testid="admin-dashboard" className="p-4 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-600 to-orange-600 flex items-center justify-center shadow-lg shadow-rose-500/20">
            <BarChart3 className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white">Tableau de Bord Admin</h1>
            <p className="text-slate-400">Analytics et comportements utilisateurs</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Period selector */}
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="period-selector"
          >
            <option value={7}>7 derniers jours</option>
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
          </select>
          
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white hover:bg-slate-700 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-white transition-colors"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Tarifs & Cotation (source unique NGAP + révision semestrielle) */}
      <CotationAdminPanel />

      {/* Base Médicaments réelle (BDPM officielle) */}
      <BdpmAdminPanel />

      {/* Contributions des médecins + corrections IA */}
      <ContributionsAdminPanel />

      {/* Blog documentaire public (SEO) */}
      <BlogAdminPanel />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Utilisateurs"
          value={analytics?.total_users || 0}
          subValue={`+${analytics?.new_users_month || 0} ce mois`}
          icon={Users}
          color="blue"
          testId="kpi-users"
        />
        <KPICard
          title="Actifs Aujourd'hui"
          value={analytics?.active_users_today || 0}
          subValue={`${analytics?.active_users_week || 0} cette semaine`}
          icon={Activity}
          color="green"
          testId="kpi-active"
        />
        <KPICard
          title="Diagnostics IA"
          value={analytics?.total_diagnoses || 0}
          subValue={`${analytics?.diagnoses_today || 0} aujourd'hui`}
          icon={Brain}
          trend={diagnosisTrend}
          testId="kpi-diagnoses"
        />
        <KPICard
          title="Analyses Dermato"
          value={analytics?.total_dermato_analyses || 0}
          subValue={`${analytics?.dermato_today || 0} aujourd'hui`}
          icon={Stethoscope}
          color="purple"
          testId="kpi-dermato"
        />
      </div>

      {/* Performance & Retention */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glassmorphic rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Temps de réponse moyen</p>
              <p className="text-2xl font-bold text-white">
                {analytics?.avg_response_time_ms ? `${(analytics.avg_response_time_ms / 1000).toFixed(1)}s` : 'N/A'}
              </p>
            </div>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all"
              style={{ width: `${Math.min((analytics?.avg_response_time_ms || 0) / 100, 100)}%` }}
            />
          </div>
        </div>

        <div className="glassmorphic rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Taux de rétention (7j)</p>
              <p className="text-2xl font-bold text-white">
                {analytics?.retention_rate?.toFixed(1) || 0}%
              </p>
            </div>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full transition-all"
              style={{ width: `${analytics?.retention_rate || 0}%` }}
            />
          </div>
        </div>

        <div className="glassmorphic rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Recherches médicales</p>
              <p className="text-2xl font-bold text-white">
                {analytics?.total_medical_searches || 0}
              </p>
            </div>
          </div>
          <p className="text-slate-500 text-sm">
            {analytics?.searches_today || 0} aujourd'hui
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Activity */}
        <div className="glassmorphic rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            Activité Quotidienne
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData}>
                <defs>
                  <linearGradient id="colorLogins" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorDiagnoses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="_id" 
                  stroke="#64748b" 
                  fontSize={12}
                  tickFormatter={(val) => val?.slice(5) || ''}
                />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="logins" 
                  stroke={COLORS.primary} 
                  fill="url(#colorLogins)"
                  name="Connexions"
                />
                <Area 
                  type="monotone" 
                  dataKey="diagnoses" 
                  stroke={COLORS.success} 
                  fill="url(#colorDiagnoses)"
                  name="Diagnostics"
                />
                <Legend />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Peak Hours */}
        <div className="glassmorphic rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            Heures de Pointe
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="hour" stroke="#64748b" fontSize={10} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill={COLORS.primary}
                  radius={[4, 4, 0, 0]}
                  name="Actions"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Feature Usage & Urgency Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feature Usage */}
        <div className="glassmorphic rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-400" />
            Fonctionnalités Utilisées
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={featureData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" stroke="#64748b" fontSize={12} />
                <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={12} width={100} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill={COLORS.secondary}
                  radius={[0, 4, 4, 0]}
                  name="Utilisations"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Urgency Distribution */}
        <div className="glassmorphic rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Distribution des Urgences
          </h3>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={urgencyData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {urgencyData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={URGENCY_COLORS[entry.level] || COLORS.chart[index % COLORS.chart.length]} 
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Pathologies & Most Active Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pathologies */}
        <div className="glassmorphic rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-emerald-400" />
            Top Pathologies Détectées
          </h3>
          <div className="space-y-3">
            {analytics?.top_pathologies?.length > 0 ? (
              analytics.top_pathologies.map((path, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-slate-900/50">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-semibold text-sm">
                      {index + 1}
                    </span>
                    <span className="text-white">{path.name}</span>
                  </div>
                  <span className="text-slate-400">{path.count} cas</span>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-8">Aucune donnée disponible</p>
            )}
          </div>
        </div>

        {/* Most Active Users */}
        <div className="glassmorphic rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Utilisateurs les Plus Actifs
          </h3>
          <div className="space-y-3">
            {userAnalytics?.most_active_users?.length > 0 ? (
              userAnalytics.most_active_users.map((user, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-slate-900/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-semibold">
                      {user.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.name || 'Utilisateur'}</p>
                      <p className="text-slate-500 text-sm">{user.email}</p>
                    </div>
                  </div>
                  <span className="text-blue-400 font-semibold">{user.actions} actions</span>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-8">Aucune donnée disponible</p>
            )}
          </div>
        </div>
      </div>

      {/* All Users List */}
      <div className="glassmorphic rounded-2xl p-6">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
          <Shield className="w-6 h-6 text-rose-400" />
          Tous les Utilisateurs ({users.length})
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Utilisateur</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Rôle</th>
                <th className="text-left py-3 px-4 text-slate-400 font-medium">Inscription</th>
                <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isSelf = currentUser && user.id === currentUser.id;
                const isBusy = busyUserId === user.id;
                return (
                <tr 
                  key={user.id} 
                  data-testid={`user-row-${user.id}`}
                  className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-semibold">
                        {user.full_name?.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-white font-medium">{user.full_name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-400">{user.email}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      user.role === 'admin' 
                        ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                        : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                    }`}>
                      {user.role === 'admin' ? 'Admin' : 'Médecin'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-500">
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-2">
                      {isSelf ? (
                        <span className="text-xs text-slate-600 italic px-2">Vous</span>
                      ) : isBusy ? (
                        <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                      ) : (
                        <>
                          {user.role === 'admin' ? (
                            <button
                              data-testid={`demote-user-${user.id}`}
                              onClick={() => updateRole(user, 'doctor')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                              title="Rétrograder en médecin"
                            >
                              <ShieldMinus className="w-3.5 h-3.5" /> Rétrograder
                            </button>
                          ) : (
                            <button
                              data-testid={`promote-user-${user.id}`}
                              onClick={() => updateRole(user, 'admin')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 transition-colors"
                              title="Promouvoir en administrateur"
                            >
                              <ShieldPlus className="w-3.5 h-3.5" /> Promouvoir
                            </button>
                          )}
                          <button
                            data-testid={`delete-user-${user.id}`}
                            onClick={() => removeUser(user)}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-colors"
                            title="Supprimer le compte"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, subValue, icon: Icon, color = 'blue', trend, testId }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-emerald-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600',
  };

  return (
    <div data-testid={testId} className="glassmorphic rounded-2xl p-5 card-hover">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend !== undefined && trend !== 0 && (
          <div className={`flex items-center gap-1 text-sm ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            {Math.abs(trend).toFixed(0)}%
          </div>
        )}
      </div>
      <h3 className="text-slate-400 text-sm font-medium mb-1">{title}</h3>
      <p className="text-3xl font-bold text-white mb-1">{value.toLocaleString()}</p>
      {subValue && <p className="text-slate-500 text-sm">{subValue}</p>}
    </div>
  );
};
